import { FirebaseError } from 'firebase/app';

/**
 * Checks if an error indicates the user cancelled the sign-in flow
 * @param error - The error to check
 * @returns true if the error indicates user cancellation
 */
export const isUserCancellationError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const code = ('code' in error && typeof error.code === 'string' ? error.code : '').toLowerCase();

    // Check for cancellation indicators
    if (
      message.includes('cancel') ||
      message.includes('cancelled') ||
      message.includes('user canceled') ||
      message.includes('user cancelled') ||
      code.includes('cancel') ||
      code.includes('cancelled') ||
      code === 'auth/cancelled' ||
      code === 'cancelled-popup-request' ||
      code === 'popup_closed_by_user' ||
      code === '1001' // Apple Sign-In cancellation code
    ) {
      return true;
    }
  }

  // Check for Firebase error codes
  if (error instanceof FirebaseError) {
    const code = error.code.toLowerCase();
    if (
      code.includes('cancel') ||
      code.includes('cancelled') ||
      code === 'auth/cancelled' ||
      code === 'auth/popup-closed-by-user'
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Handles Firebase authentication errors and returns the appropriate translation key
 * @param error - The error thrown by Firebase auth
 * @returns Translation key for the specific error
 */
export const getFirebaseAuthErrorKey = (error: unknown): string => {
  if (error instanceof FirebaseError) {
    const errorCode = error.code;
    return `firebase_errors.${errorCode}`;
  }

  // If it's not a Firebase error or doesn't have a code, return default
  return 'firebase_errors.default';
};

/**
 * Checks if the error has a translation key, otherwise returns default
 * @param error - The error thrown by Firebase auth
 * @param t - Translation function
 * @returns Translated error message
 */
export const getFirebaseAuthError = (error: unknown, t: (key: string) => string): string => {
  const errorKey = getFirebaseAuthErrorKey(error);

  // Try to get the specific error translation, fall back to default if not found
  try {
    const translation = t(errorKey);
    // If translation returns the key itself, it means the translation doesn't exist
    if (translation === errorKey) {
      return t('firebase_errors.default');
    }
    return translation;
  } catch {
    return t('firebase_errors.default');
  }
};

// Failed email/password sign-in codes. Depending on the project's email
// enumeration protection, Firebase may tell an unknown email apart from a wrong
// password, so the login screen answers all of them with one message.
const INVALID_LOGIN_CREDENTIAL_CODES = new Set([
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
]);

/**
 * Error message for email/password login that never reveals whether an
 * account exists for the address.
 */
export const getEmailLoginError = (error: unknown, t: (key: string) => string): string => {
  if (error instanceof FirebaseError && INVALID_LOGIN_CREDENTIAL_CODES.has(error.code)) {
    return t('login.error_msg_text');
  }
  return getFirebaseAuthError(error, t);
};
